import React, { useState, useEffect, useRef } from 'react';

// ==================== BACKEND CONFIGURATION ====================
// Comprehensive configuration for all analytics section features
const ANALYTICS_CONFIG = {
  // Master toggle - if false, entire section is hidden
  SHOW_ANALYTICS_SECTION: true,
  
  // ========== Tab Visibility ==========
  SHOW_UNIVERSITIES_CHART: true,
  SHOW_COUNTRIES_CHART: false,
  SHOW_INDUSTRY_CATEGORIES: true,
  SHOW_METADATA_STATS: false,
  
  // ========== University Charts Configuration ==========
  // Technologies by University Chart
  SHOW_TECH_BY_UNIVERSITY: false,
  GROUP_TECH_OTHERS: true,  // If true, shows top 10 + "Others"; if false, shows all universities
  MANUAL_TECH_TOTAL: "18000+" as string | null,  // e.g., null or "18000+" for actual count
  
  // Researchers by University Chart  
  SHOW_RESEARCHERS_BY_UNIVERSITY: false,
  GROUP_RESEARCHERS_OTHERS: true,  // If true, shows top 10 + "Others"; if false, shows all universities
  MANUAL_RESEARCHERS_TOTAL: "26000+" as string | null,  // e.g., null or "26000+" for actual count
  
  // ========== Industry Categories Configuration ==========
  SHOW_MAJOR_CATEGORIES: true,
  MANUAL_MAJOR_CATEGORIES_TOTAL: "21000+" as string | null,  // e.g., null or "19000+" for actual count
  
  SHOW_SUBCATEGORIES: false,
  MANUAL_SUBCATEGORIES_TOTAL: "50000+" as string | null,  // e.g., null or "5000+" for actual count
  
  // ========== Other Settings ==========
  TOP_CATEGORIES_COUNT: 10,  // Number of top categories to show before grouping as "Others"
  ENABLE_UNIVERSITY_WISE_CATEGORIES: false,  // Allow users to toggle university-specific category data
} as const;
// ==============================================================

// SVG Icons
const TechIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
  </svg>
);

const ResearcherIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const UniversityIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 14l9-5-9-5-9 5 9 5z"/>
    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/>
    <path d="M12 14v7"/>
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);

const IndustryIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
  </svg>
);

interface AnalyticsData {
  metadata: {
    generated_at: string;
    database_path: string;
    total_technologies: number;
    total_researchers: number;
    total_tech_managers: number;
    total_universities: number;
  };
  universities: {
    name: string;
    country: string;
    technologies: number;
    researchers: number;
    first_created: string;
    last_updated: string;
  }[];
  country_distribution: {
    [key: string]: {
      universities: number;
      technologies: number;
      researchers: number;
    };
  };
  industry_categories: {
    global: {
      major_categories: { [key: string]: number };
      subcategories: { [key: string]: number };
    };
    by_university: {
      [key: string]: {
        major_categories: { [key: string]: number };
        subcategories: { [key: string]: number };
      };
    };
  };
}

// Color palette for pie charts
const CHART_COLORS = [
  '#667eea', // Primary purple
  '#48bb78', // Green
  '#ed8936', // Orange
  '#e53e3e', // Red
  '#38b2ac', // Teal
  '#9f7aea', // Light purple
  '#f6ad55', // Light orange
  '#fc8181', // Light red
  '#4fd1c5', // Light teal
  '#b794f4', // Lighter purple
  '#f687b3', // Pink
  '#68d391', // Light green
];

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
  percentage?: number;
  fullName?: string;
}

// Interactive Pie Chart Component
const InteractivePieChart: React.FC<{
  data: ChartDataItem[];
  title: string;
  size?: number;
  onSegmentClick?: (item: ChartDataItem) => void;
  showSideBySide?: boolean;
  manualTotal?: string | null;
  actualTotal?: number;
}> = ({ data, title, size = 200, onSegmentClick, showSideBySide = false, manualTotal = null, actualTotal }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  
  const calculatedTotal = data.reduce((sum, item) => sum + item.value, 0);
  const totalForCalculation = actualTotal !== undefined ? actualTotal : calculatedTotal;
  const displayTotal = manualTotal || (actualTotal !== undefined ? actualTotal.toLocaleString() : calculatedTotal.toLocaleString());
  
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 10;
  const innerRadius = radius * 0.55;
  
  let currentAngle = -90;
  
  const segments = data.map((item) => {
    const percentage = (item.value / totalForCalculation) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);
    
    const x1Inner = centerX + innerRadius * Math.cos(startRad);
    const y1Inner = centerY + innerRadius * Math.sin(startRad);
    const x2Inner = centerX + innerRadius * Math.cos(endRad);
    const y2Inner = centerY + innerRadius * Math.sin(endRad);
    
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    const path = `
      M ${x1Inner} ${y1Inner}
      L ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
      L ${x2Inner} ${y2Inner}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1Inner} ${y1Inner}
    `;
    
    return {
      ...item,
      percentage,
      path,
    };
  });

  const handleClick = (item: ChartDataItem, index: number) => {
    setSelectedIndex(selectedIndex === index ? null : index);
    if (onSegmentClick) {
      onSegmentClick(item);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGElement>, seg: typeof segments[0], svgElement: SVGSVGElement) => {
    const rect = svgElement.getBoundingClientRect();
    const displayName = seg.fullName || seg.name;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      text: `${displayName}: ${seg.percentage.toFixed(1)}%`,
    });
  };

  return (
    <div className={showSideBySide ? "flex flex-col items-center w-full" : "flex flex-col items-center"}>
      <h4 className="text-sm font-semibold text-grayscale-1200 mb-6 text-center w-full">{title}</h4>
      
      <div className={showSideBySide 
        ? "flex flex-col lg:flex-row items-start justify-center gap-8 w-full" 
        : "flex flex-col items-center w-full"
      }>
        <div className="relative flex-shrink-0 mx-auto lg:mx-0">
          <svg 
            ref={svgRef}
            width={size} 
            height={size} 
            viewBox={`0 0 ${size} ${size}`}
            className="cursor-pointer"
            onMouseLeave={() => setTooltip(null)}
          >
            {segments.map((seg, i) => (
              <path
                key={i}
                d={seg.path}
                fill={seg.color}
                stroke="#FCFBF9"
                strokeWidth="2"
                opacity={
                  hoveredIndex === null && selectedIndex === null
                    ? 1
                    : hoveredIndex === i || selectedIndex === i
                    ? 1
                    : 0.4
                }
                transform={
                  hoveredIndex === i || selectedIndex === i
                    ? `scale(1.03) translate(${-centerX * 0.03}, ${-centerY * 0.03})`
                    : ''
                }
                style={{
                  transition: 'all 0.2s ease-out',
                  transformOrigin: `${centerX}px ${centerY}px`,
                }}
                onMouseEnter={(e) => {
                  setHoveredIndex(i);
                  if (svgRef.current) {
                    handleMouseMove(e, seg, svgRef.current);
                  }
                }}
                onMouseMove={(e) => {
                  if (svgRef.current) {
                    handleMouseMove(e, seg, svgRef.current);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredIndex(null);
                  setTooltip(null);
                }}
                onClick={() => handleClick(seg, i)}
              />
            ))}
            
            <circle cx={centerX} cy={centerY} r={innerRadius - 5} fill="#FCFBF9" />
            {/* <text
              x={centerX}
              y={centerY - 8}
              textAnchor="middle"
              className="text-lg font-bold fill-grayscale-1300"
              style={{ fontSize: '18px', fontWeight: 'bold' }}
            >
              {displayTotal}
            </text>
            <text
              x={centerX}
              y={centerY + 12}
              textAnchor="middle"
              className="text-xs fill-grayscale-700"
              style={{ fontSize: '11px' }}
            >
              Total
            </text> */}
          </svg>
          
          {tooltip && hoveredIndex !== null && (
            <div
              className="absolute pointer-events-none bg-grayscale-1300 text-white text-xs px-2 py-1 rounded shadow-lg z-10 whitespace-nowrap"
              style={{
                left: `${tooltip.x + 10}px`,
                top: `${tooltip.y - 30}px`,
              }}
            >
              {tooltip.text}
            </div>
          )}
        </div>
        
        <div className={showSideBySide ? "w-full lg:flex-1 lg:max-w-md mt-4 lg:mt-0" : "mt-4 w-full"}>
          <div className={showSideBySide 
            ? "flex flex-col text-xs max-h-[325px] overflow-y-auto w-full" 
            : "grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs max-h-[140px] overflow-y-auto w-full px-2"
          }>
            {segments.map((seg, i) => (
              <div 
                key={i} 
                className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 transition-all ${
                  hoveredIndex === i || selectedIndex === i 
                    ? 'bg-primary-100' 
                    : 'hover:bg-gray-50'
                }`}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleClick(seg, i)}
              >
                <div 
                  className="w-3 h-3 rounded-sm flex-shrink-0" 
                  style={{ backgroundColor: seg.color }}
                />
                <span 
                  className="text-grayscale-1100 flex-1 truncate" 
                  title={seg.fullName || seg.name}
                >
                  {seg.name}
                </span>
                <span className="text-grayscale-600 text-xs font-medium flex-shrink-0">
                  {seg.percentage.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
}> = ({ label, value, icon }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-primary-200/50 flex items-center gap-3 hover:shadow-md transition-shadow">
    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
      {icon}
    </div>
    <div>
      <div className="text-2xl font-bold text-grayscale-1300">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-xs text-grayscale-900">{label}</div>
    </div>
  </div>
);

const AnalyticsSection: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUniversityWise, setShowUniversityWise] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'industries'>('overview');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await fetch('/analytics_pie.json');
        
        if (!response.ok) {
          throw new Error(`Failed to load analytics data: ${response.status}`);
        }
        const data = await response.json();
        setAnalyticsData(data);
        
        if (data.universities && data.universities.length > 0) {
          setSelectedUniversity(data.universities[0].name);
        }
      } catch (err) {
        console.error('AnalyticsSection: Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (!ANALYTICS_CONFIG.SHOW_ANALYTICS_SECTION) {
    return null;
  }

  if (loading) {
    return (
      <section className="w-full py-8 sm:py-12 md:py-16 px-2 sm:px-4 overflow-x-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !analyticsData) {
    return null;
  }

  // Prepare university data with configurable grouping
  const prepareUniversityData = (
    universities: AnalyticsData['universities'], 
    sortBy: 'technologies' | 'researchers',
    groupOthers: boolean
  ) => {
    const sorted = [...universities].sort((a, b) => b[sortBy] - a[sortBy]);
    const totalValue = sorted.reduce((sum, uni) => sum + uni[sortBy], 0);
    
    if (groupOthers) {
      const topUniversities = sorted.slice(0, 10);
      const othersUniversities = sorted.slice(10);
      const othersSum = othersUniversities.reduce((sum, uni) => sum + uni[sortBy], 0);
      
      const result = topUniversities.map((uni, i) => ({
        name: uni.name
          .replace('University of ', '')
          .replace('Indian Institute of Technology ', 'IIT ')
          .replace('Massachusetts Institute of Technology', 'MIT'),
        value: uni[sortBy],
        color: CHART_COLORS[i % CHART_COLORS.length],
        fullName: uni.name,
      }));
      
      if (othersSum > 0) {
        result.push({
          name: 'Others',
          value: othersSum,
          color: '#a0aec0',
          fullName: `Other ${othersUniversities.length} universities`,
        });
      }
      
      return { data: result, totalValue };
    } else {
      const result = sorted.map((uni, i) => ({
        name: uni.name
          .replace('University of ', '')
          .replace('Indian Institute of Technology ', 'IIT ')
          .replace('Massachusetts Institute of Technology', 'MIT'),
        value: uni[sortBy],
        color: CHART_COLORS[i % CHART_COLORS.length],
        fullName: uni.name,
      }));
      
      return { data: result, totalValue };
    }
  };

  // Prepare category data with configurable totals
  const prepareCategoryData = (
    dataObj: { [key: string]: number },
    topN: number = ANALYTICS_CONFIG.TOP_CATEGORIES_COUNT
  ) => {
    const sorted = Object.entries(dataObj).sort((a, b) => b[1] - a[1]);
    const totalValue = sorted.reduce((sum, [_, val]) => sum + val, 0);
    
    const top = sorted.slice(0, topN);
    const othersSum = sorted.slice(topN).reduce((sum, [_, val]) => sum + val, 0);
    
    const result = top.map(([name, value], i) => ({
      name,
      value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
    
    if (othersSum > 0) {
      result.push({
        name: 'Others',
        value: othersSum,
        color: '#a0aec0',
      });
    }
    
    return { data: result, totalValue };
  };

  // Prepare all chart data
  const techDataResult = prepareUniversityData(
    analyticsData.universities, 
    'technologies',
    ANALYTICS_CONFIG.GROUP_TECH_OTHERS
  );
  
  const researcherDataResult = prepareUniversityData(
    analyticsData.universities,
    'researchers',
    ANALYTICS_CONFIG.GROUP_RESEARCHERS_OTHERS
  );

  const countryData = Object.entries(analyticsData.country_distribution).map(([name, data], i) => ({
    name,
    value: data.technologies,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  // Get category data based on toggle
  const getCategoryData = () => {
    if (showUniversityWise && selectedUniversity && ANALYTICS_CONFIG.ENABLE_UNIVERSITY_WISE_CATEGORIES) {
      const uniData = analyticsData.industry_categories.by_university[selectedUniversity];
      if (uniData) {
        return {
          major: prepareCategoryData(uniData.major_categories),
          sub: prepareCategoryData(uniData.subcategories),
        };
      }
    }
    return {
      major: prepareCategoryData(analyticsData.industry_categories.global.major_categories),
      sub: prepareCategoryData(analyticsData.industry_categories.global.subcategories),
    };
  };

  const categoryData = getCategoryData();

  // Determine grid layouts
  const getOverviewGridCols = () => {
    let count = 0;
    if (ANALYTICS_CONFIG.SHOW_TECH_BY_UNIVERSITY) count++;
    if (ANALYTICS_CONFIG.SHOW_RESEARCHERS_BY_UNIVERSITY) count++;
    if (ANALYTICS_CONFIG.SHOW_COUNTRIES_CHART) count++;
    
    // With side-by-side layout, use vertical stacking for better space
    if (count === 1) return 'grid-cols-1 max-w-3xl mx-auto';
    if (count === 2) return 'grid-cols-1 max-w-6xl mx-auto gap-8';
    return 'grid-cols-1 max-w-6xl mx-auto gap-8';
  };

  const getIndustryGridCols = () => {
    let count = 0;
    if (ANALYTICS_CONFIG.SHOW_MAJOR_CATEGORIES) count++;
    if (ANALYTICS_CONFIG.SHOW_SUBCATEGORIES) count++;
    
    // With side-by-side layout, use single column for better space
    if (count === 1) return 'grid-cols-1 max-w-3xl mx-auto';
    return 'grid-cols-1 max-w-6xl mx-auto gap-8';
  };

  const hasOverviewCharts = ANALYTICS_CONFIG.SHOW_TECH_BY_UNIVERSITY || 
                           ANALYTICS_CONFIG.SHOW_RESEARCHERS_BY_UNIVERSITY || 
                           ANALYTICS_CONFIG.SHOW_COUNTRIES_CHART;

  return (
    <section className="w-full py-8 sm:py-12 md:py-16 px-2 sm:px-4 bg-gradient-to-b from-white/50 to-[#FCFBF9] overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10">
          <h2 className="text-xl sm:text-2xl font-medium text-black uppercase tracking-widest mb-2 break-words">
            Our Technologies at a Glance
          </h2>
          {/* <p className="text-base text-black/70 font-grotesk"> */}
          <p className="text-xs text-grayscale-600 font-grotesk">
          
            {/* Explore cutting-edge research and innovations from top universities worldwide */}
            1500+ Technologies Added in the Last 30 Days
          </p>
        </div>

        {/* Stats Cards */}
        {ANALYTICS_CONFIG.SHOW_METADATA_STATS && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard 
              icon={<TechIcon />} 
              label="Technologies" 
              value={analyticsData.metadata.total_technologies} 
            />
            <StatCard 
              icon={<ResearcherIcon />} 
              label="Researchers" 
              value={analyticsData.metadata.total_researchers} 
            />
            <StatCard 
              icon={<UniversityIcon />} 
              label="Universities" 
              value={analyticsData.metadata.total_universities} 
            />
            <StatCard 
              icon={<GlobeIcon />} 
              label="Countries" 
              value={Object.keys(analyticsData.country_distribution).length} 
            />
          </div>
        )}

        {/* Tab Navigation - Only show if both tabs have content */}
        {hasOverviewCharts && ANALYTICS_CONFIG.SHOW_INDUSTRY_CATEGORIES && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-full p-1 shadow-sm border border-primary-200/50">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'overview'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-grayscale-900 hover:text-grayscale-1200'
                }`}
              >
                <ChartIcon />
                University Overview
              </button>
              <button
                onClick={() => setActiveTab('industries')}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'industries'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-grayscale-900 hover:text-grayscale-1200'
                }`}
              >
                <IndustryIcon />
                Industry Categories
              </button>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {(activeTab === 'overview' || !ANALYTICS_CONFIG.SHOW_INDUSTRY_CATEGORIES) && hasOverviewCharts && (
          <div className={`grid gap-8 ${getOverviewGridCols()}`}>
            {/* Technologies by University */}
            {ANALYTICS_CONFIG.SHOW_TECH_BY_UNIVERSITY && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200/50 hover:shadow-md transition-shadow">
                <InteractivePieChart 
                  data={techDataResult.data} 
                  title="Technologies by University"
                  size={300}
                  showSideBySide={true}
                  manualTotal={ANALYTICS_CONFIG.MANUAL_TECH_TOTAL}
                  actualTotal={techDataResult.totalValue}
                />
              </div>
            )}

            {/* Researchers by University */}
            {ANALYTICS_CONFIG.SHOW_RESEARCHERS_BY_UNIVERSITY && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200/50 hover:shadow-md transition-shadow">
                <InteractivePieChart 
                  data={researcherDataResult.data} 
                  title="Researchers by University"
                  size={300}
                  showSideBySide={true}
                  manualTotal={ANALYTICS_CONFIG.MANUAL_RESEARCHERS_TOTAL}
                  actualTotal={researcherDataResult.totalValue}
                />
              </div>
            )}

            {/* Countries Chart */}
            {ANALYTICS_CONFIG.SHOW_COUNTRIES_CHART && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200/50 hover:shadow-md transition-shadow">
                <InteractivePieChart 
                  data={countryData} 
                  title="Technologies by Country"
                  size={300}
                  showSideBySide={true}
                />
              </div>
            )}
          </div>
        )}

        {/* Industries Tab */}
        {(activeTab === 'industries' || !hasOverviewCharts) && ANALYTICS_CONFIG.SHOW_INDUSTRY_CATEGORIES && (
          <div>
            {/* University Toggle */}
            {ANALYTICS_CONFIG.ENABLE_UNIVERSITY_WISE_CATEGORIES && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showUniversityWise}
                    onChange={(e) => setShowUniversityWise(e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-grayscale-1100">Show University-wise Data</span>
                </label>
                
                {showUniversityWise && (
                  <select
                    value={selectedUniversity}
                    onChange={(e) => setSelectedUniversity(e.target.value)}
                    className="px-4 py-2 text-sm bg-white border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {analyticsData.universities.map((uni) => (
                      <option key={uni.name} value={uni.name}>
                        {uni.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Category subtitle */}
            {showUniversityWise && ANALYTICS_CONFIG.ENABLE_UNIVERSITY_WISE_CATEGORIES && (
              <p className="text-center text-sm text-grayscale-700 mb-6">
                Showing categories for: <span className="font-semibold text-primary-600">{selectedUniversity}</span>
              </p>
            )}

            {/* Category Charts */}
            <div className={`grid gap-6 ${getIndustryGridCols()}`}>
              {/* Major Categories */}
              {ANALYTICS_CONFIG.SHOW_MAJOR_CATEGORIES && (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200/50 hover:shadow-md transition-shadow">
                  <InteractivePieChart 
                    data={categoryData.major.data} 
                    title={`Top ${ANALYTICS_CONFIG.TOP_CATEGORIES_COUNT} Major Categories`}
                    size={300}
                    showSideBySide={true}
                    manualTotal={ANALYTICS_CONFIG.MANUAL_MAJOR_CATEGORIES_TOTAL}
                    actualTotal={categoryData.major.totalValue}
                  />
                </div>
              )}

              {/* Subcategories */}
              {ANALYTICS_CONFIG.SHOW_SUBCATEGORIES && (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-200/50 hover:shadow-md transition-shadow">
                  <InteractivePieChart 
                    data={categoryData.sub.data} 
                    title={`Top ${ANALYTICS_CONFIG.TOP_CATEGORIES_COUNT} Subcategories`}
                    size={300}
                    showSideBySide={true}
                    manualTotal={ANALYTICS_CONFIG.MANUAL_SUBCATEGORIES_TOTAL}
                    actualTotal={categoryData.sub.totalValue}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer note */}
        <div className="text-center mt-8 text-xs text-grayscale-600">
          <p>
            Data last updated: {new Date(analyticsData.metadata.generated_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
            //   day: 'numeric'
            })}
          </p>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsSection;